import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useLanguage } from '../contexts/LanguageContext';
import {
  buildCollectStepList,
  buildPreviewImages,
  buildSkuStructure,
  buildAuthModalPath,
  extractSkuInsights,
  defaultAiConfig,
  resolveCollectProgress,
  resolveApiBaseUrl,
  resolveAuthToken,
  resolveUserStage,
  loadAiConfig,
  saveAiConfig,
  type AiConfig,
  type AiConfigStyle,
  type AiConfigSeoLevel
} from '../lib/utils';

type ProcessStatus = 'idle' | 'loading' | 'success' | 'error';

export const ImportSourcePreviewScreen: React.FC = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = (location.state || {}) as {
    taskId?: string;
    collectionId?: string;
    url?: string;
  };

  const [taskId] = useState(routeState.taskId || '');
  const [collectionId, setCollectionId] = useState(routeState.collectionId || '');
  const [sourceUrl, setSourceUrl] = useState(routeState.url || '');
  const [collectStatus, setCollectStatus] = useState<'idle' | 'queued' | 'collecting' | 'completed' | 'failed'>('idle');
  const [collectMessage, setCollectMessage] = useState('');
  const [collectError, setCollectError] = useState('');
  const [collectStage, setCollectStage] = useState('');
  const [collectProgress, setCollectProgress] = useState(0);

  // Product State
  const [colorOptions, setColorOptions] = useState<string[]>([]);
  const [sizeOptions, setSizeOptions] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [mainImage, setMainImage] = useState('');
  const [title, setTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const [sourcePlatform, setSourcePlatform] = useState('');
  const [productId, setProductId] = useState('');
  const [thumbnails, setThumbnails] = useState<string[]>([]);

  // AI & Process States
  const [supplierPrice, setSupplierPrice] = useState<string>('--');
  const [priceRangeText, setPriceRangeText] = useState<string>('--');
  const [stockText, setStockText] = useState('--');
  const [skuList, setSkuList] = useState<Array<{
    name: string;
    price: number | null;
    stock: number | null;
    variantType: string;
    variantValue: string;
    attributes: Array<{ label: string; value: string }>;
  }>>([]);
  const [saveDraftStatus, setSaveDraftStatus] = useState<ProcessStatus>('idle');
  const [config, setConfig] = useState<AiConfig>({ ...defaultAiConfig });
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isConfigModalMounted, setIsConfigModalMounted] = useState(false);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [preferAdvanced, setPreferAdvanced] = useState(false);
  const userStage = useMemo(() => resolveUserStage(), []);
  const isTrialUser = userStage.stage !== 'active';

  const collectSteps = buildCollectStepList(collectStatus === 'queued' ? 'pending' : collectStatus);

  const stylePromptMap = useMemo(() => ({
    professional: t('aiConfig.promptProfessional'),
    creative: t('aiConfig.promptCreative'),
    concise: t('aiConfig.promptConcise'),
    custom: t('aiConfig.promptCustom')
  }), [t]);

  const shouldShowConfigModal = isConfigModalMounted;

  const styleOptions: Array<{ value: AiConfigStyle; label: string }> = [
    { value: 'professional', label: t('aiConfig.styleProfessional') },
    { value: 'creative', label: t('aiConfig.styleCreative') },
    { value: 'concise', label: t('aiConfig.styleConcise') }
  ];

  const seoOptions: Array<{ value: AiConfigSeoLevel; label: string; desc: string }> = [
    { value: 'standard', label: t('aiConfig.seoStandard'), desc: t('aiConfig.seoStandardDesc') },
    { value: 'deep', label: t('aiConfig.seoDeep'), desc: t('aiConfig.seoDeepDesc') }
  ];

  const currentStyleLabel = useMemo(() => {
    if (config.contentStyle === 'custom') {
      return t('aiConfig.styleCustom');
    }
    const match = styleOptions.find((option) => option.value === config.contentStyle);
    return match?.label ?? t('aiConfig.styleProfessional');
  }, [config.contentStyle, styleOptions, t]);

  const handleThumbnailClick = (src: string) => {
    setMainImage(src);
  };

  const handleEditTitle = () => {
    setIsEditingTitle(true);
    setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
    }, 10);
  };

  const updateConfig = (next: Partial<AiConfig>) => {
    setConfig((prev) => ({
      ...prev,
      ...next
    }));
  };

  const handleUpgradeClick = () => {
    if (!userStage.isAuthenticated) {
      navigate(buildAuthModalPath(location.pathname, location.search, 'login'));
      return;
    }
    navigate('/settings/subscription');
  };

  const persistConfig = (nextConfig: AiConfig) => {
    setConfig(nextConfig);
    saveAiConfig(nextConfig);
  };

  const handleLaunchOptimization = (overrideConfig?: AiConfig) => {
    const nextConfig = overrideConfig ?? config;
    persistConfig(nextConfig);
    setIsConfigModalOpen(false);
    navigate('/studio', { state: { collectionId, config: nextConfig } });
  };

  const handleOpenAdvancedSettings = () => {
    persistConfig(config);
    if (typeof window !== 'undefined') {
      localStorage.setItem('ai_config_preference', 'advanced');
    }
    setPreferAdvanced(true);
    setIsConfigModalOpen(false);
    navigate('/capability-settings', {
      state: {
        from: location.pathname,
        collectionId,
        taskId,
        url: sourceUrl,
        config
      }
    });
  };

  const handleStartOptimization = () => {
    if (preferAdvanced) {
      handleOpenAdvancedSettings();
      return;
    }
    setIsConfigModalOpen(true);
  };

  const handleSaveDraft = () => {
      setSaveDraftStatus('loading');
      setTimeout(() => {
          setSaveDraftStatus('success');
          setTimeout(() => setSaveDraftStatus('idle'), 2000);
      }, 1000);
  }

  useEffect(() => {
    const storedConfig = loadAiConfig();
    setConfig(storedConfig);
    if (typeof window !== 'undefined') {
      setPreferAdvanced(localStorage.getItem('ai_config_preference') === 'advanced');
    }
  }, []);

  useEffect(() => {
    if (isConfigModalOpen) {
      setIsConfigModalMounted(true);
      const raf = window.requestAnimationFrame(() => setIsConfigModalVisible(true));
      return () => window.cancelAnimationFrame(raf);
    }
    if (isConfigModalMounted) {
      setIsConfigModalVisible(false);
      const timer = window.setTimeout(() => {
        setIsConfigModalMounted(false);
      }, 220);
      return () => window.clearTimeout(timer);
    }
  }, [isConfigModalOpen, isConfigModalMounted]);

  useEffect(() => {
    if (!isConfigModalMounted) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsConfigModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isConfigModalMounted]);

  useEffect(() => {
    if (!taskId) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    const mapStatus = (status?: string) => {
      if (!status) return 'queued';
      if (status === 'completed') return 'completed';
      if (['failed', 'cancelled', 'expired'].includes(status)) return 'failed';
      if (['pending', 'assigned'].includes(status)) return 'queued';
      return 'collecting';
    };

    const normalizeStatus = (status?: string) => status || 'pending';

    const pollTask = async () => {
      if (cancelled) return;

      const token = resolveAuthToken();
      if (!token) {
        setCollectStatus('failed');
        setCollectMessage(t('importPreview.authRequired'));
        return;
      }

      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/collect/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setCollectStatus('failed');
          setCollectMessage(payload?.detail?.error || t('importPreview.pollFailed'));
          return;
        }

        const task = payload?.task;
        const normalizedStatus = normalizeStatus(task?.status);
        const status = mapStatus(normalizedStatus);
        setCollectStatus(status);
        setCollectMessage(task?.message || '');
        setCollectError(task?.error_message || '');
        setCollectStage(task?.stage || '');
        setCollectProgress(resolveCollectProgress(normalizedStatus, task?.progress));
        if (task?.collection_id && !collectionId) {
          setCollectionId(task.collection_id);
        }

        if (!cancelled && status !== 'completed' && status !== 'failed') {
          timer = window.setTimeout(pollTask, 2000);
        }
      } catch (error) {
        setCollectStatus('failed');
        setCollectMessage(t('importPreview.pollFailed'));
      }
    };

    pollTask();

    return () => {
      cancelled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [taskId, collectionId, t]);

  useEffect(() => {
    if (!collectionId) {
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      const token = resolveAuthToken();
      if (!token) {
        setCollectStatus('failed');
        setCollectMessage(t('importPreview.authRequired'));
        return;
      }

      try {
        const apiBaseUrl = resolveApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/preview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) {
          return;
        }

        if (payload?.title) {
          setTitle(payload.title);
        }
        if (payload?.source_url) {
          setSourceUrl(payload.source_url);
        }
        if (payload?.platform) {
          setSourcePlatform(payload.platform === '1688' ? '1688.com' : payload.platform);
        }
        if (payload?.product_id) {
          setProductId(payload.product_id);
        }

        const images = buildPreviewImages(payload);
        if (images.length > 0) {
          setThumbnails(images);
          setMainImage(images[0]);
        }

        const skuInsights = extractSkuInsights(payload?.extracted_data);
        if (skuInsights.price !== null && Number.isFinite(skuInsights.price)) {
          setSupplierPrice(skuInsights.price.toFixed(2));
        } else {
          setSupplierPrice('--');
        }
        if (skuInsights.stock !== null) {
          setStockText(`${Math.round(skuInsights.stock).toLocaleString()} Units`);
        } else {
          setStockText('--');
        }
        if (skuInsights.colors.length > 0) {
          setColorOptions(skuInsights.colors);
          setSelectedColor(skuInsights.colors[0]);
        } else {
          setColorOptions([]);
          setSelectedColor('');
        }
        if (skuInsights.sizes.length > 0) {
          setSizeOptions(skuInsights.sizes);
          setSelectedSize(skuInsights.sizes[0]);
        } else {
          setSizeOptions([]);
          setSelectedSize(null);
        }
        const priceRange = payload?.extracted_data?.price_range;
        if (priceRange) {
          setPriceRangeText(String(priceRange));
        } else {
          setPriceRangeText('--');
        }

        setSkuList(buildSkuStructure(payload?.extracted_data));

        setCollectStatus('completed');
      } catch (error) {
        setCollectMessage(t('importPreview.previewFailed'));
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [collectionId, t]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Header
        breadcrumbs={[
          { label: t('importPreview.breadcrumb'), path: '/workspace' },
          { label: t('importPreview.nav') }
        ]}
      />

      {taskId && (
        <div className="bg-slate-900 text-white border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
            <div className="flex items-center gap-3">
              <span className="font-bold">{t('importPreview.collectStatus')}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                collectStatus === 'completed'
                  ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40'
                  : collectStatus === 'failed'
                    ? 'bg-red-500/20 text-red-200 border border-red-400/40'
                    : 'bg-blue-500/20 text-blue-200 border border-blue-400/40'
              }`}>
                {collectStatus === 'completed'
                  ? t('importPreview.collectCompleted')
                  : collectStatus === 'failed'
                    ? t('importPreview.collectFailed')
                    : collectStatus === 'queued'
                      ? t('importPreview.collectQueued')
                      : t('importPreview.collecting')}
              </span>
            </div>
            <div className="text-xs text-slate-200">
              {collectMessage || (collectStatus === 'failed' ? collectError : t('importPreview.collectingHint'))}
            </div>
          </div>
        </div>
      )}

      {taskId && (
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{t('importPreview.collectProgressTitle')}</h3>
                <p className="text-sm text-slate-500">
                  {collectMessage || t('importPreview.collectProgressHint')}
                </p>
              </div>
              <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <span>{t('importPreview.progressLabel')}</span>
                <span className="text-primary">{collectProgress}%</span>
              </div>
            </div>

            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${collectStatus === 'failed' ? 'bg-red-500' : 'bg-primary'}`}
                style={{ width: `${collectProgress}%` }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {collectSteps.map((step, index) => {
                const stepLabelMap: Record<string, string> = {
                  queued: t('importPreview.stepQueued'),
                  assigned: t('importPreview.stepAssigned'),
                  collecting: t('importPreview.stepCollecting'),
                  uploading: t('importPreview.stepUploading'),
                  completed: t('importPreview.stepCompleted')
                };
                const isActive = step.status === 'active';
                const isDone = step.status === 'done';
                const isError = step.status === 'error';

                return (
                  <div
                    key={`${step.key}-${index}`}
                    className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                      isError
                        ? 'border-red-200 bg-red-50 text-red-600'
                        : isDone
                          ? 'border-green-200 bg-green-50 text-green-700'
                          : isActive
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-slate-50 text-slate-400'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isDone && <span className="material-symbols-outlined text-[18px]">check_circle</span>}
                      {isError && <span className="material-symbols-outlined text-[18px]">error</span>}
                      {isActive && <span className="material-symbols-outlined text-[18px]">pending</span>}
                      {!isDone && !isError && !isActive && <span className="material-symbols-outlined text-[18px]">schedule</span>}
                      <span>{stepLabelMap[step.key] || step.key}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3">
              <span className="font-semibold text-slate-600">{t('importPreview.currentStage')}</span>
              <span>
                {(() => {
                  const stageKey = collectStage ? `importPreview.stage.${collectStage}` : 'importPreview.stage.pending';
                  const stageLabel = t(stageKey);
                  if (stageLabel === stageKey) {
                    return collectStage || t('importPreview.stage.pending');
                  }
                  return stageLabel;
                })()}
              </span>
              {collectStatus === 'failed' && collectError && (
                <span className="text-red-500 font-semibold">{collectError}</span>
              )}
            </div>
          </div>
        </section>
      )}

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Gallery Sidebar */}
          <div className="w-full lg:w-24 shrink-0">
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[calc(100vh-250px)] custom-scrollbar pb-4 lg:pb-0">
              {thumbnails.length > 0 ? (
                thumbnails.map((src, index) => (
                  <div
                    key={index}
                    onClick={() => handleThumbnailClick(src)}
                    className={`relative w-20 h-20 rounded-xl border-2 overflow-hidden cursor-pointer shrink-0 transition-all duration-200 ${
                      mainImage === src
                        ? 'border-primary ring-2 ring-primary/20 scale-105'
                        : 'border-slate-200 hover:border-primary/50'
                    } bg-white`}
                  >
                    <img
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover transition-opacity"
                      src={src}
                    />
                  </div>
                ))
              ) : (
                <div className="w-20 h-20 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-xs text-center p-2">
                  {t('importPreview.noImages')}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
                      {t('importPreview.source')}: {sourcePlatform}
                    </span>
                    <span className="text-xs text-slate-400">ID: {productId}</span>
                  </div>
                  <div className="relative group max-w-2xl">
                    <textarea
                      ref={titleInputRef}
                      className={`w-full text-2xl font-bold bg-transparent border-none rounded-xl p-2 -ml-2 resize-none leading-tight transition-all ${
                          isEditingTitle
                          ? 'ring-2 ring-primary focus:ring-primary bg-slate-50'
                          : 'focus:ring-2 focus:ring-primary/20 hover:bg-slate-50'
                      }`}
                      rows={2}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={() => setIsEditingTitle(false)}
                      readOnly={!isEditingTitle}
                    />
                    {!isEditingTitle && (
                      <button
                        onClick={handleEditTitle}
                        className="absolute top-2 -right-8 p-1 rounded-full hover:bg-slate-100 text-slate-300 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Main Image */}
                <div className="aspect-square rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 relative group flex items-center justify-center">
                  {mainImage ? (
                    <>
                      <img
                        alt="Main Product"
                        className="w-full h-full object-contain mix-blend-multiply transition-all duration-300 group-hover:scale-105"
                        src={mainImage}
                      />
                      <div className="absolute bottom-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-50 transition-colors">
                            <span className="material-symbols-outlined text-sm">zoom_in</span>
                         </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-slate-400 text-sm px-6">
                      <span className="material-symbols-outlined text-3xl mb-2">image_not_supported</span>
                      <p>{t('importPreview.noImages')}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">sell</span>
                    {t('importPreview.supplierPrice')}
                  </h3>
                  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-slate-200">
                        <span className="text-sm text-slate-500">{t('importPreview.supplierPrice')}</span>
                        <span className="font-bold text-slate-700">
                          {supplierPrice !== '--' ? `$${supplierPrice}` : '--'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-slate-500">{t('importPreview.priceRange')}</span>
                        <span className="font-bold text-slate-700">
                          {priceRangeText !== '--' ? `$${priceRangeText}` : '--'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"></div>
          </div>

          <aside className="w-full lg:w-80 space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-primary">view_list</span>
                <h3 className="font-bold text-slate-900">{t('importPreview.skuStructure')}</h3>
              </div>
              {skuList.length > 0 ? (
                <div className="space-y-3">
                  {skuList.slice(0, 6).map((sku, index) => (
                    <div key={`${sku.name}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-slate-900">{sku.name}</span>
                        <span className="text-sm font-bold text-slate-900">
                          {sku.price !== null ? `$${sku.price.toFixed(2)}` : '--'}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>{t('importPreview.skuStock')}: {sku.stock !== null ? Math.round(sku.stock).toLocaleString() : '--'}</span>
                        {(sku.variantType || sku.variantValue) && (
                          <span>
                            {t('importPreview.skuVariant')}: {sku.variantType ? `${sku.variantType} ${sku.variantValue}`.trim() : sku.variantValue}
                          </span>
                        )}
                      </div>
                      {sku.attributes.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {sku.attributes.slice(0, 3).map((attr) => (
                            <span key={`${sku.name}-${attr.label}`} className="px-2.5 py-1 rounded-full bg-white text-xs text-slate-600 border border-slate-200">
                              {attr.label}: {attr.value}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">{t('importPreview.noSkuData')}</p>
              )}
            </div>
          </aside>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white px-6 py-4 z-[60]">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
                onClick={handleSaveDraft}
                className="px-6 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-all active:scale-95 flex items-center gap-2"
            >
                {saveDraftStatus === 'loading' ? (
                     <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></span>
                ) : saveDraftStatus === 'success' ? (
                    <>
                        <span className="material-symbols-outlined text-[18px]">check</span>
                        {t('importPreview.saved')}
                    </>
                ) : (
                    t('importPreview.saveDraft')
                )}
            </button>

            <div className="flex flex-col items-start gap-1">
              <button
                  onClick={handleStartOptimization}
                  className="px-6 py-3 bg-primary text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all flex items-center gap-2 active:scale-95"
              >
                  <span className="material-symbols-outlined text-[20px]">auto_fix_high</span>
                  {t('importPreview.startOptimization')}
              </button>
              {isTrialUser && (
                <span className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-amber-600">
                  <span className="material-symbols-outlined text-[14px]">lock</span>
                  <span>{t('aiConfig.trialImageBadge')}</span>
                  <button
                    onClick={handleUpgradeClick}
                    className="text-amber-700 font-bold hover:text-amber-800 underline underline-offset-4"
                  >
                    {t('aiConfig.trialUpgradeCta')}
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </footer>

      {shouldShowConfigModal && (
        <div
          className={`fixed inset-0 z-[110] flex items-center justify-center p-4 transition-all duration-300 ${
            isConfigModalVisible ? 'bg-slate-900/60 backdrop-blur-sm opacity-100' : 'bg-slate-900/0 opacity-0'
          }`}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsConfigModalOpen(false);
            }
          }}
        >
          <div
            className={`bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] transition-all duration-300 ${
              isConfigModalVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">{t('aiConfig.modalTitle')}</h3>
                <p className="text-sm text-slate-500 mt-1">{t('aiConfig.modalSubtitle')}</p>
              </div>
              <button
                onClick={() => setIsConfigModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>
            <div className="p-8 space-y-6 overflow-y-auto">
              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  {t('aiConfig.targetPlatform')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button className="relative p-4 rounded-2xl border-2 border-primary bg-blue-50/50 cursor-pointer transition-all text-left">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-slate-900">Ozon</span>
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-[14px]">check</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">{t('aiConfig.platformOzonDesc')}</p>
                  </button>
                  <div className="relative p-4 rounded-2xl border border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-slate-400">Amazon</span>
                      <span className="px-2 py-0.5 rounded bg-slate-200 text-[10px] font-bold text-slate-500 uppercase">
                        {t('aiConfig.comingSoon')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">{t('aiConfig.platformAmazonDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                    {t('aiConfig.targetLanguage')}
                  </label>
                  <select
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all"
                    value={config.language}
                    onChange={(event) => updateConfig({ language: event.target.value as AiConfig['language'] })}
                  >
                    <option value="ru">{t('aiConfig.languageRu')}</option>
                    <option value="en">{t('aiConfig.languageEn')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                    {t('aiConfig.contentStyle')}
                  </label>
                  <div className="flex p-1 bg-slate-100 rounded-xl">
                    {styleOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => updateConfig({ contentStyle: option.value })}
                        className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${
                          config.contentStyle === option.value
                            ? 'bg-white shadow-sm text-primary'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                    <button
                      onClick={() => updateConfig({ contentStyle: 'custom' })}
                      className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${
                        config.contentStyle === 'custom'
                          ? 'bg-white shadow-sm text-primary'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {t('aiConfig.styleCustom')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    {t('aiConfig.stylePrompt')}
                  </label>
                  <div className="h-px flex-1 bg-slate-100"></div>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-[16px] text-primary">terminal</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                      {t('aiConfig.currentStyle')} {currentStyleLabel}
                    </span>
                  </div>
                  <code className="block text-[11px] text-slate-600 leading-relaxed">
                    {stylePromptMap[config.contentStyle]}
                  </code>
                  {config.contentStyle === 'custom' && (
                    <button
                      onClick={handleOpenAdvancedSettings}
                      className="mt-3 text-xs font-semibold text-primary hover:underline"
                    >
                      {t('aiConfig.customHint')}
                    </button>
                  )}
                </div>
              </div>

              <div className="p-5 rounded-2xl border border-blue-100 bg-blue-50/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-primary">
                      <span className="material-symbols-outlined">image</span>
                    </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{t('aiConfig.imageGeneration')}</h4>
                  <p className="text-[11px] text-slate-500">{t('aiConfig.imageGenerationDesc')}</p>
                  {isTrialUser && (
                    <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-[10px] font-bold text-amber-600 uppercase tracking-tighter">
                      <span className="material-symbols-outlined text-[12px]">lock</span>
                      {t('aiConfig.trialImageBadge')}
                    </span>
                  )}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      checked={config.enableImageGeneration}
                      onChange={(event) => updateConfig({ enableImageGeneration: event.target.checked })}
                      className="sr-only peer"
                      type="checkbox"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-white/70 rounded-lg">
                <span className="material-symbols-outlined text-[14px] text-amber-500">database</span>
                <span className="text-[10px] font-medium text-slate-500">{t('aiConfig.imageCredits')}</span>
              </div>
              {isTrialUser && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-amber-600">
                  <span className="material-symbols-outlined text-[14px]">info</span>
                  <span>{t('aiConfig.trialImageHint')}</span>
                  <button
                    onClick={handleUpgradeClick}
                    className="text-amber-700 font-bold hover:text-amber-800 underline underline-offset-4"
                  >
                    {t('aiConfig.trialUpgradeCta')}
                  </button>
                </div>
              )}
            </div>

              <div>
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 block">
                  {t('aiConfig.seoLevel')}
                </label>
                <div className="space-y-3">
                  {seoOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-4 rounded-xl border cursor-pointer transition-colors ${
                        config.seoLevel === option.value
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        className="w-4 h-4 text-primary border-slate-300 focus:ring-primary"
                        name="seo"
                        type="radio"
                        checked={config.seoLevel === option.value}
                        onChange={() => updateConfig({ seoLevel: option.value })}
                      />
                      <div className="ml-4 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="block text-sm font-bold">{option.label}</span>
                          {option.value === 'deep' && (
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-[10px] font-bold text-green-600 uppercase tracking-tighter">
                              {t('aiConfig.recommended')}
                            </span>
                          )}
                        </div>
                        <span className="block text-xs text-slate-500">{option.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex flex-col gap-4">
              <button
                onClick={() => handleLaunchOptimization()}
                className="w-full py-4 bg-primary text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-500/30 hover:bg-blue-700 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 group"
              >
                <span className="material-symbols-outlined text-2xl group-hover:rotate-12 transition-transform">rocket_launch</span>
                {t('aiConfig.launch')}
              </button>
              <div className="flex items-center justify-between px-2 text-sm">
                <button
                  onClick={() => setIsConfigModalOpen(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors font-bold"
                >
                  {t('aiConfig.cancel')}
                </button>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      const next = { ...defaultAiConfig };
                      handleLaunchOptimization(next);
                    }}
                    className="text-slate-500 hover:text-slate-700 transition-colors font-bold"
                  >
                    {t('aiConfig.useDefault')}
                  </button>
                  <button
                    onClick={handleOpenAdvancedSettings}
                    className="text-primary hover:underline decoration-2 underline-offset-4 font-bold"
                  >
                    {t('aiConfig.advanced')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
